/**
 * Lightweight in-memory cache + in-flight de-duplication for GET requests.
 *
 * Why: the Dashboard (home) page and its ~25 analytics chart components each
 * fire their own API calls on mount. With no caching, every time the user
 * navigates away and back the whole page re-fetches from the network, showing
 * spinners and a multi-second wait each time.
 *
 * This helper sits transparently in front of the GET request chokepoints
 * (the shared axios `api` instance and the `fetch`-based service helpers) so
 * that:
 *   1. Identical requests fired at the same time share ONE network call
 *      (de-duplication) — cuts the initial burst on first load.
 *   2. Repeated requests within the TTL are served instantly from memory —
 *      revisiting the page no longer re-fetches everything.
 *
 * The cache is in-memory only (resets on a full page reload), so there is no
 * risk of stale data leaking across sessions, and the request flow is
 * unchanged — callers still get a Promise that resolves to the same data.
 */

type CacheEntry = { ts: number; value: unknown };

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

const store = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<unknown>>();

/**
 * Run `loader` through the cache. A fresh cached value (within `ttl`) is
 * returned immediately; an identical request already in flight is shared;
 * otherwise the loader runs and its result is cached. Rejections are never
 * cached.
 */
export function cachedRequest<T>(
  key: string,
  loader: () => Promise<T>,
  ttl: number = DEFAULT_TTL
): Promise<T> {
  const hit = store.get(key);
  if (hit && Date.now() - hit.ts < ttl) {
    return Promise.resolve(hit.value as T);
  }

  const pending = inflight.get(key);
  if (pending) {
    return pending as Promise<T>;
  }

  const promise = loader()
    .then((value) => {
      store.set(key, { ts: Date.now(), value });
      inflight.delete(key);
      return value;
    })
    .catch((err) => {
      inflight.delete(key);
      throw err;
    });

  inflight.set(key, promise);
  return promise;
}

/** Clear the entire request cache (e.g. on logout). */
export function clearRequestCache(): void {
  store.clear();
  inflight.clear();
}
